import React, { ComponentProps, ComponentType, CSSProperties, ForwardedRef, forwardRef, SVGProps } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { BadgeSize, ComponentWithBadge } from '@hh.ru/magritte-ui-badge';
import IconDynamic from '@hh.ru/magritte-ui-icon/IconDynamic';
import { SkeletonSize16, SkeletonSize24, SkeletonSize32 } from '@hh.ru/magritte-ui-icon/Skeleton';
import {
    IconWrapperComponent,
    IconWrapperComponentSize16,
    IconWrapperComponentSize24,
    IconWrapperComponentSize32,
} from '@hh.ru/magritte-ui-icon/iconTypes';

import styles from './icon.less';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { ref?: ForwardedRef<SVGSVGElement> }>;
type SizedIconWrapperComponent<T extends string> = T extends `${string}Size16`
    ? IconWrapperComponentSize16
    : T extends `${string}Size24`
      ? IconWrapperComponentSize24
      : T extends `${string}Size32`
        ? IconWrapperComponentSize32
        : IconWrapperComponent;

const INCOMPLETE_SKELETON_BY_SIZE = {
    16: SkeletonSize16,
    24: SkeletonSize24,
    32: SkeletonSize32,
};

type SizedSkeleton<T extends string> = T extends `${string}Size16`
    ? IconWrapperComponentSize16<{ loading?: boolean }>
    : T extends `${string}Size24`
      ? IconWrapperComponentSize24<{ loading?: boolean }>
      : T extends `${string}Size32`
        ? IconWrapperComponentSize32<{ loading?: boolean }>
        : IconWrapperComponent<{ loading?: boolean }>;

type IconWithSkeleton<T extends string> = SizedIconWrapperComponent<T> & { Skeleton: SizedSkeleton<T> };

const ALLOWED_BADGE_SIZES: BadgeSize[] = ['small', 'extra-small'];

const hoc = function <T extends string>(Icon: IconComponent, componentName: string): IconWithSkeleton<T> {
    const IconWrapperComponent = (
        {
            initialColor: _initialColor,
            'aria-label': ariaLabel,
            'aria-hidden': ariaHidden,
            padding,
            borderRadius,
            borderStyle,
            backgroundStyle,
            shadow = 'level-0',
            increaseShadow,
            badge,
            onClick,
            disabled,
            hex,
            ...props
        }: Omit<ComponentProps<IconWrapperComponent>, 'ref'>,
        ref: ForwardedRef<HTMLOrSVGElement>
    ) => {
        let highlightedValue;
        let disabledValue;
        let pressedValue;
        let hexHighlightedValue;
        let hexDisabledValue;
        let hexPressedValue;
        let dataQa;
        const initialColor = !_initialColor && !hex ? 'primary' : _initialColor;
        let propsToPass = { ...props };
        const withWrapper =
            !!padding ||
            !!borderRadius ||
            !!backgroundStyle ||
            !!borderStyle ||
            !!onClick ||
            shadow !== 'level-0' ||
            increaseShadow;
        const WrapperElement = onClick ? 'button' : 'span';
        const additionalProps: Record<string, unknown> = { ref };
        const isComponentDisabled = useDisabled() || !!disabled;

        if (WrapperElement === 'button') {
            additionalProps.type = 'button';
            additionalProps.disabled = isComponentDisabled;
            additionalProps.onClick = onClick;
        }

        if ('highlightedColor' in propsToPass) {
            const { highlightedColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            highlightedValue = highlightedColor;
        }

        if ('disabledColor' in propsToPass) {
            const { disabledColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            disabledValue = disabledColor;
        }

        if ('pressedColor' in propsToPass) {
            const { pressedColor, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            pressedValue = pressedColor;
        }

        if (highlightedValue && !pressedValue) {
            pressedValue = highlightedValue;
        }

        if ('hexHighlighted' in propsToPass) {
            const { hexHighlighted, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexHighlightedValue = hexHighlighted;
        }

        if ('hexDisabled' in propsToPass) {
            const { hexDisabled, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexDisabledValue = hexDisabled;
        }

        if ('hexPressed' in propsToPass) {
            const { hexPressed, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            hexPressedValue = hexPressed;
        }

        if (hexHighlightedValue && !hexPressedValue) {
            hexPressedValue = hexHighlightedValue;
        }

        if ('data-qa' in propsToPass) {
            const { 'data-qa': dataQaValue, ...lastProps } = propsToPass;
            propsToPass = lastProps;
            dataQa = dataQaValue;
        }

        const icon = (
            <Icon
                {...propsToPass}
                aria-label={ariaLabel}
                aria-hidden={ariaLabel ? ariaHidden || undefined : true}
                data-qa={withWrapper && dataQa ? `${dataQa}-svg` : dataQa}
                role="img"
                focusable="false"
                ref={!withWrapper ? (ref as ForwardedRef<SVGSVGElement>) : undefined}
                className={classnames(styles.icon, {
                    [styles[`icon_initial-${initialColor}`]]: !!initialColor,
                    [styles[`icon_highlighted-${highlightedValue}`]]: !!highlightedValue,
                    [styles[`icon_pressed-${pressedValue}`]]: !!pressedValue,
                    [styles[`icon_disabled-${disabledValue}`]]: !!disabledValue,
                    [styles.hexed]: hex || hexHighlightedValue || hexDisabledValue,
                })}
                style={
                    {
                        '--magritte-ui-icon-hex-color': hex,
                        '--magritte-ui-icon-hex-highlighted-color': hexHighlightedValue,
                        '--magritte-ui-icon-hex-pressed-color': hexPressedValue,
                        '--magritte-ui-icon-hex-disabled-color': hexDisabledValue,
                    } as CSSProperties
                }
            />
        );

        const wrappedIcon =
            badge && !backgroundStyle && shadow === 'level-0' && !isComponentDisabled ? (
                <ComponentWithBadge allowedSizes={ALLOWED_BADGE_SIZES} badge={badge} offset={-3}>
                    {icon}
                </ComponentWithBadge>
            ) : (
                icon
            );

        let wrapper = wrappedIcon;

        if (withWrapper) {
            const content = (
                <WrapperElement
                    {...additionalProps}
                    data-qa={dataQa}
                    className={classnames(styles.view, {
                        [styles[`shadow-${shadow}`]]: shadow,
                        [styles.increase]: increaseShadow,
                    })}
                    style={{ borderRadius: borderRadius === 'halfHeight' ? '9999px' : borderRadius }}
                >
                    <span
                        className={classnames(
                            styles.view,
                            backgroundStyle && styles[`style-${backgroundStyle}`],
                            borderStyle && styles[`border-style-${borderStyle}`]
                        )}
                        style={{ borderRadius: borderRadius === 'halfHeight' ? '9999px' : borderRadius, padding }}
                    >
                        {wrappedIcon}
                    </span>
                </WrapperElement>
            );

            wrapper = (
                <span
                    className={classnames(styles.wrapper, {
                        [styles.active]: !!onClick,
                    })}
                >
                    {onClick ? <IconDynamic>{content}</IconDynamic> : content}
                </span>
            );
        }

        if (badge && (backgroundStyle || shadow !== 'level-0') && !isComponentDisabled) {
            return (
                <ComponentWithBadge allowedSizes={ALLOWED_BADGE_SIZES} badge={badge} offset={0}>
                    {wrapper}
                </ComponentWithBadge>
            );
        }

        return wrapper;
    };

    const IconWrapper = forwardRef(IconWrapperComponent) as IconWrapperComponent;

    const size = parseInt(componentName.slice(-2), 10) as 16 | 24 | 32;
    const IncompleteSkeleton = INCOMPLETE_SKELETON_BY_SIZE[size];

    const Skeleton = ((props: ComponentProps<typeof IconWrapper> & { loading: boolean }) => (
        <IncompleteSkeleton Icon={IconWrapper} {...props} />
    )) as SizedSkeleton<T>;

    const IconWithSkeleton = IconWrapper as IconWithSkeleton<T>;
    IconWithSkeleton.isIcon = true;
    IconWithSkeleton.Skeleton = Skeleton;
    IconWithSkeleton._size = size;

    return IconWithSkeleton;
};

export default hoc;
